import { computed, createApp, defineComponent, h, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { CATALOG_PAGE_SIZE, pcApi, type AnyRecord } from './api'
import { createHeroMediaCoordinator } from './heroMediaCoordinator'
import { resolveContentBadge } from './contentBadge'
import { buildPcVipPlanPresentation } from './vipPlanPresentation'
import { h5UrlWithAttribution, persistPcAttributionFromUrl } from './googleAds'
import './style.css'

type ViewName = 'home' | 'discover' | 'latest' | 'categories' | 'search' | 'category' | 'ranking' | 'detail' | 'player' | 'membership' | 'continue' | 'rewards' | 'profile' | 'signin'
interface RouteState { view: ViewName; params: Record<string, string> }

const APP_DOWNLOAD_URL = 'https://www.reelora.vip/get-app.html'
const IOS_STORE_URL = 'https://apps.apple.com/us/app/reelora/id6777937368'
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.novaplayx.app'
const CONTACT_EMAIL = 'media@reelora.vip'

function parseRoute(path = location.pathname, query = location.search): RouteState {
  const parts = path.split('/').filter(Boolean)
  if (parts[0] === 'watch') return { view: 'player', params: { drama: parts[1] || '', episode: parts[2] || '1' } }
  if (parts[0] === 'detail') return { view: 'detail', params: { drama: parts[1] || '' } }
  if (parts[0] === 'category') return { view: 'category', params: { category: parts[1] || '' } }
  if (parts[0] === 'ranking') return { view: 'ranking', params: { period: parts[1] || 'daily' } }
  if (parts[0] === 'search') return { view: 'search', params: { query: new URLSearchParams(query).get('q') || '' } }
  if (['discover', 'latest', 'categories', 'membership', 'continue', 'rewards', 'profile', 'signin'].includes(parts[0])) return { view: parts[0] as ViewName, params: {} }
  return { view: 'home', params: {} }
}

function unwrapModules(home: AnyRecord) {
  return {
    heroes: (home?.modules?.banner?.data || []).slice(0, 5),
    trending: home?.modules?.hot?.data || [],
    updated: home?.modules?.new?.data || [],
    editorial: home?.modules?.featured?.data || [],
    categories: home?.categories || [],
  }
}

function emptyFeed() {
  return { list: [] as AnyRecord[], page: 0, total: 0, loading: false, exhausted: false }
}

function isRemoteImageUrl(value: unknown) {
  return /^https?:\/\//.test(String(value || '').trim())
}

function formatSocialCount(count: unknown): string {
  const n = Math.max(0, Number(count) || 0)
  if (n <= 0) return ''
  if (n < 1000) return String(n)
  if (n < 1000000) {
    const value = Math.floor(n / 100) / 10
    return value % 1 === 0 ? `${Math.floor(value)}K` : `${value}K`
  }
  const value = Math.floor(n / 100000) / 10
  return value % 1 === 0 ? `${Math.floor(value)}M` : `${value}M`
}

function pickPositiveCount(record: AnyRecord, keys: string[]): number {
  for (const key of keys) {
    const n = Number(record?.[key])
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

function playCountOf(drama: AnyRecord): number {
  return pickPositiveCount(drama, ['total_views', 'totalViews', 'play_count', 'playCount'])
}

function socialStatLine(drama: AnyRecord): string {
  return [
    { label: 'plays', value: playCountOf(drama) },
    { label: 'likes', value: pickPositiveCount(drama, ['total_likes', 'totalLikes', 'like_count', 'likeCount']) },
    { label: 'favorites', value: pickPositiveCount(drama, ['total_favorites', 'totalFavorites']) },
    { label: 'shares', value: pickPositiveCount(drama, ['total_shares', 'totalShares']) },
  ].filter(item => item.value > 0).map(item => `${formatSocialCount(item.value)} ${item.label}`).join(' · ')
}

const App = defineComponent({
  setup() {
    const route = ref<RouteState>(parseRoute())
    const loading = ref(false)
    const error = ref('')
    const data = reactive<AnyRecord>({})
    const heroIndex = ref(0)
    const heroPaused = ref(false)
    const homeVideo = ref<HTMLVideoElement | null>(null)
    const homeMediaPaused = ref(false)
    const playerVideo = ref<HTMLVideoElement | null>(null)
    const playerMediaPaused = ref(true)
    const searchDraft = ref(route.value.params.query || '')
    const downloadOpen = ref(false)
    let heroTimer: ReturnType<typeof setInterval> | null = null
    let playerSignedUrlRetried = false
    let playerErrorBoundEl: HTMLVideoElement | null = null

    function bindPlayerErrorRefresh() {
      const el = playerVideo.value
      if (!el || el === playerErrorBoundEl) return
      playerErrorBoundEl = el
      el.addEventListener('error', () => {
        void refreshPlayerSignedUrlOnce()
      })
    }

    async function refreshPlayerSignedUrlOnce() {
      if (playerSignedUrlRetried || route.value.view !== 'player') return
      const episodes = data.detail?.episodes || []
      const selected = episodes.find((item: AnyRecord) => String(item.episode_num) === route.value.params.episode) || episodes[0]
      if (!selected?.id || !canWatchEpisode(selected)) return
      playerSignedUrlRetried = true
      try {
        data.play = await pcApi.play(selected.id)
        data.playLocked = false
        await nextTick()
        bindPlayerErrorRefresh()
        const video = playerVideo.value
        if (video && playableSource(data.play)) {
          video.load()
          await video.play().catch(() => undefined)
        }
      } catch {
        data.play = null
        data.playLocked = !canWatchEpisode(selected)
      }
    }

    const modules = computed(() => unwrapModules(data.home || {}))
    const currentHero = computed(() => modules.value.heroes[heroIndex.value])
    const vipPlan = computed(() => buildPcVipPlanPresentation(data.vipPlans))

    function rankingDramas() {
      return (data.ranking?.list || []).map((item: AnyRecord) => ({ ...(item.drama || {}), __rank: item.rank_num })).filter((drama: AnyRecord) => drama.id)
    }
    function themeShelves() {
      return (data.genreShelves || []).filter((shelf: AnyRecord) => (shelf.items || []).length > 0)
    }
    function catalogFeed(name: string) {
      if (!data[name] || typeof data[name] !== 'object') data[name] = emptyFeed()
      return data[name] as { list: AnyRecord[]; page: number; total: number; loading: boolean; exhausted: boolean; key: string }
    }
    function catalogStatus(feed: { list: AnyRecord[]; total: number; loading: boolean; exhausted: boolean }) {
      if (feed.loading) return h('div', { class: 'catalog-status' }, [h('span', { class: 'spinner' }), h('p', 'Loading more stories…')])
      if (feed.exhausted && feed.list.length) {
        const total = feed.total || feed.list.length
        return h('div', { class: 'catalog-status catalog-status--done' }, [h('p', `Showing all ${total} playable dramas`)])
      }
      return h('div', { class: 'catalog-sentinel', 'aria-hidden': 'true' })
    }
    function mergeUniqueDramas(existing: AnyRecord[], incoming: AnyRecord[]) {
      const idOf = (item: AnyRecord) => String(item?.id || item?.drama?.id || '')
      const seen = new Set(existing.map(idOf).filter(Boolean))
      const next = [...existing]
      for (const item of incoming) {
        const id = idOf(item)
        if (!id || seen.has(id)) continue
        seen.add(id)
        next.push(item)
      }
      return next
    }
    async function loadGenreShelves(categories: AnyRecord[]) {
      const picks = (categories || []).slice(0, 4)
      const rows: AnyRecord[] = []
      for (const cat of picks) {
        const detail = await pcApi.category(String(cat.id), 1, 24).catch(() => null)
        const items = detail?.dramas?.list || []
        if (items.length) rows.push({ cat, items })
      }
      data.genreShelves = rows
    }
    async function fetchCatalogPage(kind: 'discover' | 'latest' | 'category' | 'ranking' | 'search', page: number) {
      if (kind === 'discover') {
        const payload = await pcApi.ranking('all_time', page, CATALOG_PAGE_SIZE)
        if (payload?.fallback) throw new Error('Verified ranking data is not available for this time window')
        const list = (payload?.list || []).map((item: AnyRecord) => ({ ...(item.drama || {}), __rank: item.rank_num })).filter((drama: AnyRecord) => drama.id)
        return { list, total: Number(payload?.total || list.length), meta: payload }
      }
      if (kind === 'latest') {
        const payload = await pcApi.browse(page, CATALOG_PAGE_SIZE)
        return { list: payload?.list || [], total: Number(payload?.total || 0), meta: payload }
      }
      if (kind === 'category') {
        const payload = await pcApi.category(route.value.params.category, page, CATALOG_PAGE_SIZE)
        return { list: payload?.dramas?.list || [], total: Number(payload?.dramas?.total || 0), meta: payload }
      }
      if (kind === 'ranking') {
        const payload = await pcApi.ranking(route.value.params.period || 'daily', page, CATALOG_PAGE_SIZE)
        if (payload?.fallback) throw new Error('Verified ranking data is not available for this time window')
        return { list: payload?.list || [], total: Number(payload?.total || (payload?.list || []).length), meta: payload }
      }
      const query = route.value.params.query || ''
      const payload = query ? await pcApi.search(query, page, CATALOG_PAGE_SIZE) : { list: [], total: 0 }
      return { list: payload?.list || [], total: Number(payload?.total || 0), meta: payload }
    }
    async function loadCatalog(kind: 'discover' | 'latest' | 'category' | 'ranking' | 'search', reset = false) {
      const feedName = kind === 'discover' ? 'discoverFeed'
        : kind === 'latest' ? 'latestFeed'
        : kind === 'category' ? 'categoryFeed'
        : kind === 'ranking' ? 'rankingFeed'
        : 'searchFeed'
      const feed = catalogFeed(feedName)
      const key = kind === 'category' ? `category:${route.value.params.category}`
        : kind === 'ranking' ? `ranking:${route.value.params.period || 'daily'}`
        : kind === 'search' ? `search:${route.value.params.query || ''}`
        : kind
      if (reset || feed.key !== key) {
        Object.assign(feed, { ...emptyFeed(), key })
      }
      if (feed.loading || feed.exhausted) return
      const nextPage = feed.page + 1
      feed.loading = true
      try {
        const result = await fetchCatalogPage(kind, nextPage)
        if (kind === 'category') data.category = result.meta
        if (kind === 'ranking') data.ranking = result.meta
        if (kind === 'search') data.search = result.meta
        const before = feed.list.length
        feed.list = mergeUniqueDramas(feed.list, result.list)
        feed.page = nextPage
        feed.total = result.total || feed.list.length
        const grew = feed.list.length > before
        feed.exhausted = result.list.length === 0 || !grew || feed.list.length >= feed.total || result.list.length < CATALOG_PAGE_SIZE
      } finally {
        feed.loading = false
      }
      await nextTick()
      maybeLoadMoreCatalog()
    }
    function maybeLoadMoreCatalog() {
      const view = route.value.view
      if (!['discover', 'latest', 'category', 'ranking', 'search'].includes(view)) return
      if (window.innerHeight + window.scrollY < document.documentElement.scrollHeight - 720) return
      void loadCatalog(view as 'discover' | 'latest' | 'category' | 'ranking' | 'search', false).catch(() => undefined)
    }
    function onWindowScroll() {
      maybeLoadMoreCatalog()
    }
    function skeletonRail() {
      return h('section', { class: 'shelf skeleton-shelf' }, [
        h('div', { class: 'section-heading' }, [h('div', [h('h2', { class: 'skeleton-line skeleton-line--title' }, ' '), h('p', { class: 'skeleton-line' }, ' ')])]),
        h('div', { class: 'poster-rail' }, Array.from({ length: 8 }, () => h('div', { class: 'poster-card skeleton-card' }, [h('span', { class: 'poster-media skeleton-poster' })]))),
      ])
    }
    function homeSkeleton() {
      return h('main', { class: 'page' }, [h('section', { class: 'desktop-theater skeleton-hero' }), skeletonRail(), skeletonRail()])
    }
    function qrDock() {
      return h('aside', { class: 'qr-dock', 'aria-label': 'Download the Reelora app' }, [
        h('img', { src: '/brand/reelora-app-download-qr.png', alt: 'Scan to download the Reelora app' }),
        h('strong', 'Scan to get the App'),
      ])
    }
    function genreLine(drama: AnyRecord) {
      return (drama.categories || []).map((item: AnyRecord) => item.name).filter(Boolean).slice(0, 2).join(' · ')
    }
    function episodeCountLabel(drama: AnyRecord) {
      const count = Number(drama.total_episodes ?? drama.episode_total ?? drama.episode_count ?? drama.episodes_count)
      return Number.isFinite(count) && count > 0 ? `${count} episodes` : ''
    }
    function navActive(label: string) {
      const view = route.value.view
      if (label === 'Home') return view === 'home'
      if (label === 'Discover') return ['discover', 'categories', 'category', 'search'].includes(view)
      if (label === 'Latest') return view === 'latest'
      if (label === 'Ranking') return view === 'ranking'
      return false
    }
    function isVipAccount() {
      const user = data.profile?.userInfo || data.profile || {}
      return Boolean(user.vip || user.is_vip || user.vip_expire_time)
    }
    /** Match App canPlayEpisode: unlock flag wins; free_switch=1 is also playable. */
    function episodeUnlocked(episode: AnyRecord | null | undefined) {
      if (!episode) return false
      if (episode.is_unlocked === true || episode.isUnlocked === true) return true
      if (Number(episode.free_switch) === 1 || episode.freeSwitch === true) return true
      return false
    }
    function canWatchEpisode(episode: AnyRecord | null | undefined) {
      return episodeUnlocked(episode) || isVipAccount()
    }
    function episodeAccessLabel(episode: AnyRecord, compact = false) {
      if (canWatchEpisode(episode)) return compact ? 'PLAY' : 'Free'
      return compact ? 'VIP' : 'Locked'
    }
    function hasViewData() {
      const view = route.value.view
      if (view === 'home' || view === 'categories') return Boolean(data.home)
      if (view === 'discover') return catalogFeed('discoverFeed').list.length > 0 || catalogFeed('discoverFeed').exhausted
      if (view === 'latest') return catalogFeed('latestFeed').list.length > 0 || catalogFeed('latestFeed').exhausted
      if (view === 'ranking') return catalogFeed('rankingFeed').list.length > 0 || data.ranking != null
      if (view === 'search') return catalogFeed('searchFeed').list.length > 0 || data.search != null
      if (view === 'category') return catalogFeed('categoryFeed').list.length > 0 || data.category != null
      if (view === 'detail' || view === 'player') return Boolean(data.detail?.drama?.id || data.detail?.episodes?.length)
      if (view === 'continue') return data.history != null
      if (view === 'rewards') return data.rewards != null
      return true
    }
    function playerVipOffer(locked: boolean) {
      return h('section', { class: locked ? 'player-lock-overlay' : 'player-membership-inline' }, [
        h('div', [
          h('strong', locked ? 'This episode is locked' : 'Reelora VIP'),
          h('small', locked
            ? (vipPlan.value.available ? `${vipPlan.value.primary} · ${vipPlan.value.renewal}` : 'Start VIP to unlock every episode')
            : (vipPlan.value.available ? `${vipPlan.value.primary} · ${vipPlan.value.renewal}` : vipPlan.value.secondary)),
        ]),
        h('button', { onClick: () => go('/membership') }, 'Start VIP'),
      ])
    }
    function playerLockStage(poster: string, dramaTitle: string, episodeNum: unknown) {
      return h('section', { class: 'player-lock-stage' }, [
        poster ? h('img', { class: 'player-lock-poster', src: poster, alt: '' }) : null,
        h('div', { class: 'player-lock-scrim' }),
        h('div', { class: 'player-lock-copy' }, [
          h('span', 'VIP EPISODE'),
          h('h2', 'Unlock to keep watching'),
          h('p', `${dramaTitle || 'This story'} · Episode ${episodeNum || ''}`),
          h('small', vipPlan.value.available ? `${vipPlan.value.primary} · ${vipPlan.value.renewal}` : 'Unlimited dramas with Reelora VIP'),
          h('button', { class: 'primary compact', onClick: () => go('/membership') }, 'Start VIP'),
        ]),
      ])
    }

    function heroDramaId(hero: AnyRecord) {
      return String(hero?.drama_id || hero?.drama?.id || hero?.id || '')
    }
    function playableSource(payload: AnyRecord) {
      return payload?.videoUrl || payload?.video_url || payload?.video_file || payload?.qualities?.hd?.url || ''
    }
    function resolveHeroBadgeLabel(drama: AnyRecord) {
      return resolveContentBadge({
        featuredSwitch: drama?.featured_switch ?? drama?.featuredSwitch,
        hotSwitch: drama?.hot_switch ?? drama?.hotSwitch,
        newSwitch: drama?.new_switch ?? drama?.newSwitch,
        vipSwitch: drama?.vip_switch ?? drama?.vipSwitch,
      })?.label || 'SPOTLIGHT'
    }
    type HeroMediaTarget = { hero: AnyRecord; index: number }
    const heroMediaCoordinator = createHeroMediaCoordinator<HeroMediaTarget, AnyRecord, AnyRecord & { id: number }, AnyRecord>({
      dramaId: target => heroDramaId(target.hero),
      detail: dramaId => pcApi.detail(dramaId),
      firstEpisode: detail => (detail?.episodes || [])[0],
      play: episodeId => pcApi.play(episodeId),
      clear: () => { data.homeMedia = null },
      commit: selection => {
        data.homeMedia = selection
        heroIndex.value = selection.hero.index
      },
    })
    async function loadHeroMedia(index: number) {
      const hero = modules.value.heroes[index]
      if (!hero) return false
      return heroMediaCoordinator.select({ hero, index })
    }
    async function selectHero(index: number) {
      await loadHeroMedia(index)
    }
    async function toggleHomeMedia() {
      const video = homeVideo.value
      if (!video) return
      if (video.paused) await video.play()
      else video.pause()
    }
    async function togglePlayerMedia() {
      const video = playerVideo.value
      if (!video) return
      if (video.paused) await video.play()
      else video.pause()
    }

    function go(path: string) {
      heroMediaCoordinator.invalidate()
      history.pushState({}, '', path)
      route.value = parseRoute()
      void load()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    function onPop() { route.value = parseRoute(); void load() }
    function dramaCard(drama: AnyRecord, meta = '', landscape = false) {
      const badge = resolveContentBadge({
        featuredSwitch: drama.featured_switch ?? drama.featuredSwitch,
        hotSwitch: drama.hot_switch ?? drama.hotSwitch,
        newSwitch: drama.new_switch ?? drama.newSwitch,
        vipSwitch: drama.vip_switch ?? drama.vipSwitch,
        continueWatching: Boolean(drama.__episode),
      })
      const playLabel = formatSocialCount(playCountOf(drama))
      return h('button', { class: ['poster-card', { landscape }], onClick: () => go(`/detail/${drama.id}`) }, [
        h('span', { class: 'poster-media' }, [
          h('img', { src: (landscape ? drama.cover_horizontal_image : drama.cover_vertical_image) || drama.cover_vertical_image || drama.cover || '', alt: drama.title || 'Reelora drama' }),
          badge ? h('span', { class: ['card-badge', `card-badge--${badge.type}`] }, badge.label) : null,
          playLabel ? h('span', { class: 'poster-play' }, [h('i', { class: 'poster-play-icon', 'aria-hidden': 'true' }), h('em', playLabel)]) : null,
        ]),
        h('strong', drama.title || 'Untitled story'),
        h('span', meta || genreLine(drama) || 'Short drama'),
      ])
    }
    function membershipInline() {
      return h('section', { class: 'membership-inline' }, [
        h('div', [h('span', 'REELORA VIP'), h('h2', 'Keep every story going'), h('p', vipPlan.value.secondary)]),
        h('button', { onClick: () => go('/membership') }, 'Start VIP'),
      ])
    }
    function storeLink(kind: 'apple' | 'google') {
      const isApple = kind === 'apple'
      return h('a', {
        class: 'store-link',
        href: isApple ? IOS_STORE_URL : ANDROID_STORE_URL,
        target: '_blank',
        rel: 'noopener noreferrer',
      }, [
        h('img', { src: isApple ? '/icons/apple.png' : '/icons/google.png', alt: '' }),
        h('span', [h('small', isApple ? 'Download on the' : 'GET IT ON'), h('strong', isApple ? 'App Store' : 'Google Play')]),
      ])
    }
    function appDownloadCard(compact = false) {
      return h('section', { class: ['app-download-card', { 'app-download-card--compact': compact }] }, [
        h('div', { class: 'app-download-qr' }, [
          h('img', { src: '/brand/reelora-app-download-qr.png', alt: 'Scan to download the Reelora app' }),
        ]),
        h('div', { class: 'app-download-copy' }, [
          h('span', 'WATCH ANYWHERE'),
          h('h2', compact ? 'Take Reelora with you' : 'Stories this good should travel'),
          h('p', compact ? 'Scan to keep watching in the Reelora app.' : 'Scan once, then continue on iPhone, iPad or Android with your Reelora library close at hand.'),
          h('div', { class: 'store-links' }, [storeLink('apple'), storeLink('google')]),
        ]),
      ])
    }
    function downloadDialog() {
      if (!downloadOpen.value) return null
      return h('div', { class: 'download-dialog-layer', onClick: (event: Event) => { if (event.target === event.currentTarget) downloadOpen.value = false } }, [
        h('section', { class: 'download-dialog', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Download Reelora' }, [
          h('button', { class: 'download-dialog-close', 'aria-label': 'Close download dialog', onClick: () => { downloadOpen.value = false } }, '×'),
          appDownloadCard(),
          h('a', { class: 'download-fallback', href: APP_DOWNLOAD_URL, target: '_blank', rel: 'noopener noreferrer' }, 'Open the Reelora download page'),
        ]),
      ])
    }
    function libraryFilters(active: 'popular' | 'latest' | 'categories' = 'popular') {
      return h('div', { class: 'library-filters', 'aria-label': 'Library filters' }, [
        h('button', { class: { active: active === 'popular' }, onClick: () => go('/discover') }, 'Popular'),
        h('button', { class: { active: active === 'latest' }, onClick: () => go('/latest') }, 'Latest'),
        h('button', { class: { active: active === 'categories' }, onClick: () => go('/categories') }, 'All genres'),
      ])
    }
    function libraryHeading(title: string, active: 'popular' | 'latest' | 'categories' = 'popular', kicker = 'REELORA LIBRARY', detail = '') {
      return h('div', { class: 'library-heading' }, [
        h('div', [h('span', kicker), h('h1', title), detail ? h('p', detail) : null]),
        libraryFilters(active),
      ])
    }
    function shelf(title: string, subtitle: string, items: AnyRecord[], action?: () => void, landscape = false, className = '') {
      return h('section', { class: 'shelf' }, [
        h('div', { class: 'section-heading' }, [h('div', [h('h2', title), h('p', subtitle)]), action ? h('button', { onClick: action }, 'See all') : null]),
        h('div', { class: ['poster-rail', className, { landscape }] }, items.map(item => dramaCard(item, '', landscape))),
      ])
    }
    function statePanel() {
      if (loading.value) return h('main', { class: 'state-panel' }, [h('span', { class: 'spinner' }), h('p', 'Curating Reelora stories…')])
      if (error.value) return h('main', { class: 'state-panel' }, [h('h1', 'This page needs another take'), h('p', error.value), h('button', { class: 'primary', onClick: load }, 'Retry')])
      return null
    }
    async function load() {
      loading.value = true
      error.value = ''
      try {
        if (!data.vipPlansResolved) {
          data.vipPlansResolved = true
          data.vipPlans = await pcApi.vipPlans().catch(() => ({ vip_packages: [] }))
        }
        const next = route.value
        if (next.view === 'home' || next.view === 'categories') {
          data.home = await pcApi.home()
          heroIndex.value = 0
          const historyPromise = pcApi.history().catch(() => ({ list: [] }))
          const rankingPromise = pcApi.ranking('daily').catch(() => ({ list: [] }))
          const shelvesPromise = next.view === 'home'
            ? loadGenreShelves(unwrapModules(data.home).categories)
            : Promise.resolve()
          await Promise.all([
            loadHeroMedia(0),
            historyPromise.then(history => { data.history = history }),
            rankingPromise.then(ranking => { data.ranking = ranking }),
            shelvesPromise,
          ])
        }
        else if (next.view === 'discover') {
          await loadCatalog('discover', true)
        }
        else if (next.view === 'latest') {
          await loadCatalog('latest', true)
        }
        else if (next.view === 'search') {
          await loadCatalog('search', true)
        }
        else if (next.view === 'category') {
          await loadCatalog('category', true)
        }
        else if (next.view === 'ranking') {
          await loadCatalog('ranking', true)
        } else if (next.view === 'detail' || next.view === 'player') {
          data.detail = await pcApi.detail(next.params.drama)
          if (!data.profile) {
            data.profile = await pcApi.profile().catch(() => ({}))
          }
          if (next.view === 'player') {
            const episodes = data.detail?.episodes || []
            const selected = episodes.find((item: AnyRecord) => String(item.episode_num) === next.params.episode) || episodes[0]
            if (!selected) throw new Error('This episode is not available')
            playerSignedUrlRetried = false
            playerErrorBoundEl = null
            data.play = null
            if (!canWatchEpisode(selected)) {
              data.playLocked = true
            } else {
              data.playLocked = false
              try {
                data.play = await pcApi.play(selected.id)
              } catch (cause) {
                const message = cause instanceof Error ? cause.message : ''
                if (/permission|vip|unlock|login/i.test(message)) {
                  data.playLocked = true
                  data.play = null
                } else {
                  throw cause
                }
              }
              await nextTick()
              bindPlayerErrorRefresh()
            }
          }
        }
        else if (next.view === 'continue') {
          const [historyData, profileData] = await Promise.all([pcApi.history(), pcApi.profile().catch(() => ({}))])
          data.history = historyData
          data.profile = profileData
        }
        else if (next.view === 'rewards') data.rewards = await pcApi.rewards()
        else if (next.view === 'membership') {
          const [homeData, profileData] = await Promise.all([pcApi.home(), pcApi.profile().catch(() => ({}))])
          data.home = homeData
          data.profile = profileData
        }
        else if (next.view === 'profile') data.profile = await pcApi.profile()
      } catch (cause) { error.value = cause instanceof Error ? cause.message : 'Reelora could not load this page' }
      finally { loading.value = false }
    }
    function submitSearch(event: Event) {
      event.preventDefault()
      const query = searchDraft.value.trim()
      if (query) go(`/search?q=${encodeURIComponent(query)}`)
    }

    function homeView() {
      const hero = currentHero.value
      const media = data.homeMedia || {}
      const source = playableSource(media.play)
      const currentDrama = media.detail?.drama || modules.value.trending.find((item: AnyRecord) => String(item.id) === heroDramaId(hero)) || hero
      const upNext = [...modules.value.heroes.filter((_: AnyRecord, index: number) => index !== heroIndex.value), ...modules.value.trending].filter((item: AnyRecord, index: number, all: AnyRecord[]) => all.findIndex(candidate => heroDramaId(candidate) === heroDramaId(item)) === index).slice(0, 1)
      const continueWatching = (data.history?.list || []).map((item: AnyRecord) => ({ ...item.drama, __episode: item.watch_progress?.episode_num || 1 }))
      return h('main', { class: 'page' }, [
        hero ? h('section', { class: 'desktop-theater', onMouseenter: () => { heroPaused.value = true }, onMouseleave: () => { heroPaused.value = false } }, [
          h('div', { class: 'media-stage' }, [
            source
              ? h('video', { ref: homeVideo, class: 'hero-video', src: source, poster: currentDrama?.cover_horizontal_image || currentDrama?.cover_vertical_image || hero.image, controls: true, autoplay: true, muted: true, playsinline: true, preload: 'metadata', 'aria-label': `${currentDrama?.title || hero.title} preview`, onPlay: () => { homeMediaPaused.value = false }, onPause: () => { homeMediaPaused.value = true } })
              : h('img', { class: 'hero-still', src: currentDrama?.cover_horizontal_image || currentDrama?.cover_vertical_image || hero.image, alt: currentDrama?.title || hero.title }),
            h('div', { class: 'media-caption' }, [
              h('span', resolveHeroBadgeLabel(currentDrama || hero)),
              h('h1', currentDrama?.title || hero.title),
              currentDrama?.description ? h('p', currentDrama.description) : null,
              h('div', { class: 'media-actions' }, [
                h('button', { class: 'primary compact', onClick: () => go(`/watch/${heroDramaId(hero)}/1`) }, 'Watch episode 1'),
                source ? h('button', { class: 'secondary compact preview-toggle', onClick: () => { void toggleHomeMedia() } }, homeMediaPaused.value ? 'Play preview' : 'Pause preview') : null,
              ]),
            ]),
            h('div', { class: 'hero-dots' }, modules.value.heroes.map((_: AnyRecord, index: number) => h('button', { 'aria-label': `Play spotlight ${index + 1}`, class: { active: index === heroIndex.value }, onClick: () => { void selectHero(index) } }, String(index + 1)))),
          ]),
          h('aside', { class: 'up-next' }, [
            h('div', { class: 'up-next-heading' }, [h('span', 'UP NEXT'), h('strong', 'Reelora picks')]),
            ...upNext.map((item: AnyRecord) => h('button', { class: 'up-next-card', onClick: () => { const index = modules.value.heroes.findIndex((heroItem: AnyRecord) => heroDramaId(heroItem) === heroDramaId(item)); if (index >= 0) void selectHero(index); else go(`/detail/${heroDramaId(item)}`) } }, [
              h('img', { src: item.cover_vertical_image || item.image || item.cover_horizontal_image || '', alt: item.title || 'Reelora drama' }),
              h('span', [h('strong', item.title || 'Reelora story'), h('small', (item.categories || []).map((category: AnyRecord) => category.name).slice(0, 1).join('') || 'Short drama')]),
            ])),
          ]),
        ]) : null,
        h('section', { class: 'home-download-strip' }, [
          h('div', [h('span', 'REELORA APP'), h('strong', 'Your next episode is already waiting.')]),
          h('p', 'Scan the code to move from the big screen to your phone without losing the story.'),
          h('button', { onClick: () => { downloadOpen.value = true } }, 'Scan to download'),
        ]),
        continueWatching.length ? shelf('Continue Watching', 'Pick up where you left off', continueWatching.slice(0, 8), () => go('/continue'), true, 'continue-rail') : null,
        shelf('Trending Now', 'What everyone is watching', modules.value.trending, () => go('/discover')),
        shelf('New Releases', 'Fresh on Reelora', modules.value.updated, () => go('/latest')),
        rankingDramas().length ? shelf('Top Charts', 'Today’s ranking', rankingDramas(), () => go('/ranking/daily')) : null,
        h('section', { class: 'genre-section' }, [h('div', { class: 'section-heading' }, [h('div', [h('h2', 'Browse by genre'), h('p', 'Jump in by mood')]), h('button', { onClick: () => go('/categories') }, 'See all')]), h('div', { class: 'genre-grid' }, modules.value.categories.map((item: AnyRecord) => h('button', { onClick: () => go(`/category/${item.id}`) }, [isRemoteImageUrl(item.icon) ? h('img', { src: item.icon, alt: '' }) : null, h('span', item.name)])))]),
        ...themeShelves().map((shelfItem: AnyRecord) => shelf(shelfItem.cat.name, 'More in this mood', shelfItem.items, () => go(`/category/${shelfItem.cat.id}`))),
        modules.value.editorial.length ? shelf("Editor's Picks", 'Worth a watch tonight', modules.value.editorial) : null,
      ])
    }
    function discoverView() {
      const feed = catalogFeed('discoverFeed')
      const total = feed.total || feed.list.length
      return h('main', { class: 'page content-page' }, [
        libraryHeading('Discover', 'popular', 'REELORA LIBRARY', total ? `${total} popular dramas · scroll for more` : ''),
        isVipAccount() ? null : membershipInline(),
        h('div', { class: 'poster-grid' }, feed.list.map((item: AnyRecord) => dramaCard(item))),
        catalogStatus(feed),
      ])
    }
    function latestView() {
      const feed = catalogFeed('latestFeed')
      const total = feed.total || feed.list.length
      return h('main', { class: 'page content-page' }, [
        libraryHeading('Latest', 'latest', 'REELORA LIBRARY', total ? `${total} playable dramas · scroll for more` : ''),
        h('div', { class: 'poster-grid' }, feed.list.map((item: AnyRecord) => dramaCard(item))),
        catalogStatus(feed),
      ])
    }
    function categoriesView() { return h('main', { class: 'page content-page' }, [
      libraryHeading('Categories', 'categories'),
      h('div', { class: 'categories-grid' }, modules.value.categories.map((item: AnyRecord, index: number) => {
        const fallback = modules.value.trending[index % Math.max(1, modules.value.trending.length)] || {}
        const image = item.cover_horizontal_image || item.cover_image || (isRemoteImageUrl(item.icon) ? item.icon : '') || fallback.cover_horizontal_image || fallback.cover_vertical_image || ''
        const count = item.drama_count ?? item.dramaCount ?? item.count
        return h('button', { class: 'category-card', onClick: () => go(`/category/${item.id}`) }, [
          image ? h('img', { src: image, alt: '' }) : null,
          h('span', { class: 'category-card__scrim' }),
          h('span', { class: 'category-card__copy' }, [h('strong', item.name), h('small', count != null ? `${count} playable dramas` : 'Open playable dramas')]),
        ])
      })),
    ]) }
    function searchView() {
      const feed = catalogFeed('searchFeed')
      const total = feed.total || feed.list.length
      return h('main', { class: 'page content-page' }, [
        h('div', { class: 'library-heading search-heading' }, [
          h('div', [h('span', 'SEARCH RESULTS'), h('h1', route.value.params.query ? `“${route.value.params.query}”` : 'Search Reelora')]),
          h('p', `${total} playable dramas · title and synopsis match · scroll for more`),
        ]),
        h('div', { class: 'poster-grid' }, feed.list.map((item: AnyRecord) => dramaCard(item))),
        catalogStatus(feed),
      ])
    }
    function categoryView() {
      const feed = catalogFeed('categoryFeed')
      const payload = data.category || {}
      const total = feed.total || payload.dramas?.total || feed.list.length
      return h('main', { class: 'page content-page' }, [
        h('div', { class: 'page-title' }, [
          h('span', 'CATEGORY'),
          h('h1', payload.category?.name || 'Category'),
          h('p', `${total} playable stories · scroll for more`),
        ]),
        h('div', { class: 'poster-grid' }, feed.list.map((item: AnyRecord) => dramaCard(item))),
        catalogStatus(feed),
      ])
    }
    function rankingView() {
      const feed = catalogFeed('rankingFeed')
      const payload = data.ranking || {}
      const periods = [
        { id: 'daily', label: 'Daily' },
        { id: 'weekly', label: 'Weekly' },
        { id: 'all_time', label: 'All time' },
      ]
      const currentPeriod = route.value.params.period || 'daily'
      const rows = feed.list.length ? feed.list : (payload.list || [])
      return h('main', { class: 'page content-page' }, [
        h('div', { class: 'library-heading ranking-heading' }, [h('div', [h('span', 'TODAY IN THE US'), h('h1', 'Ranking'), h('p', `${feed.total || rows.length || 0} ranked dramas · scroll for more`)])]),
        h('div', { class: 'period-tabs', 'aria-label': 'Ranking period' }, periods.map(period => h('button', { class: { active: currentPeriod === period.id }, onClick: () => go(`/ranking/${period.id}`) }, period.label))),
        h('div', { class: 'ranking-list ranking-grid' }, rows.map((item: AnyRecord, index: number) => {
          const drama = item.drama || item
          const rankNum = item.rank_num || item.__rank || index + 1
          const plays = formatSocialCount(playCountOf(drama))
          const meta = [plays ? `${plays} plays` : '', genreLine(drama), episodeCountLabel(drama)].filter(Boolean).join(' · ') || 'Short drama'
          return h('button', { onClick: () => go(`/detail/${drama.id}`) }, [
            h('strong', String(rankNum).padStart(2, '0')),
            h('img', { src: drama.cover_vertical_image, alt: drama.title }),
            h('div', [h('h3', drama.title), h('p', meta)]),
            h('small', Number(item.rank_change) > 0 ? `Up ${item.rank_change}` : 'Stable'),
          ])
        })),
        catalogStatus(feed),
      ])
    }
    function detailView() { const payload = data.detail || {}; const drama = payload.drama || {}; const episodes = payload.episodes || []; const stats = socialStatLine(drama); const firstPlayable = episodes.find((episode: AnyRecord) => canWatchEpisode(episode)) || episodes[0]; return h('main', { class: 'page detail-page' }, [h('img', { class: 'detail-backdrop', src: drama.cover_horizontal_image || drama.cover_vertical_image, alt: '' }), h('div', { class: 'detail-scrim' }), h('div', { class: 'detail-copy' }, [h('span', 'REELORA SERIES'), h('h1', drama.title || 'Story'), h('p', drama.description || drama.subtitle || ''), stats ? h('p', { class: 'detail-stats' }, stats) : null, h('button', { class: 'primary', disabled: !episodes.length, onClick: () => episodes.length && go(`/watch/${drama.id}/${firstPlayable?.episode_num || 1}`) }, episodes.length ? 'Start watching' : 'Episodes coming soon')]), h('section', { class: 'episode-list' }, episodes.map((episode: AnyRecord) => h('button', { class: { 'episode-locked': !canWatchEpisode(episode) }, onClick: () => go(`/watch/${drama.id}/${episode.episode_num}`) }, [h('strong', `EP ${episode.episode_num}`), h('span', episode.title || 'Episode'), h('small', episodeAccessLabel(episode))])))]) }
    function playerView() {
      const payload = data.detail || {}
      const episodes = payload.episodes || []
      const selected = episodes.find((item: AnyRecord) => String(item.episode_num) === route.value.params.episode) || episodes[0]
      const locked = Boolean(selected) && (Boolean(data.playLocked) || !canWatchEpisode(selected))
      const source = locked ? '' : playableSource(data.play)
      const poster = selected?.thumbnail_image || payload.drama?.cover_horizontal_image || payload.drama?.cover_vertical_image
      const freeCount = episodes.filter((episode: AnyRecord) => episodeUnlocked(episode)).length
      return h('main', { class: 'player-page' }, [
        h('section', { class: 'player-column' }, [
          h('div', { class: 'player-topline' }, [
            h('button', { class: 'back-link', onClick: () => go(`/detail/${payload.drama?.id || route.value.params.drama}`) }, '← Back to series'),
            h('span', `${episodes.length} episodes · ${freeCount} free · ${locked ? 'VIP locked' : 'Playing'}`),
          ]),
          h('section', { class: 'player-stage' }, [
            locked
              ? playerLockStage(poster, payload.drama?.title || 'Reelora', selected?.episode_num)
              : (source && selected
                ? h('video', { ref: (el: unknown) => { playerVideo.value = el as HTMLVideoElement | null; bindPlayerErrorRefresh() }, class: 'player-video', src: source, poster, controls: true, autoplay: true, playsinline: true, preload: 'auto', 'aria-label': `${payload.drama?.title || 'Reelora'} episode ${selected.episode_num}`, onPlay: () => { playerMediaPaused.value = false }, onPause: () => { playerMediaPaused.value = true }, onError: () => { void refreshPlayerSignedUrlOnce() } })
                : h('section', { class: 'player-unavailable' }, [h('img', { src: poster, alt: '' }), h('h2', 'This episode is not ready to play'), h('p', 'The poster stays visible while Reelora checks the media source.'), h('button', { class: 'secondary', onClick: () => { playerSignedUrlRetried = false; void load() } }, 'Retry episode')])),
          ]),
          h('div', { class: 'player-caption player-caption--below' }, [h('span', resolveHeroBadgeLabel(payload.drama || {})), h('h1', `${payload.drama?.title || 'Reelora'} · Episode ${selected?.episode_num || ''}`), h('p', locked ? 'VIP required · Unlock to continue' : (source ? 'Portrait cinema · Sound follows your device setting' : 'Poster retained · Episode context preserved'))]),
          locked || isVipAccount() ? null : playerVipOffer(false),
          h('section', { class: 'player-meta' }, [
            h('div', [h('span', locked ? 'LOCKED' : 'NOW PLAYING'), h('h1', payload.drama?.title || 'Reelora'), h('p', `Episode ${selected?.episode_num || ''} · ${payload.drama?.description || 'A Reelora short drama.'}`), socialStatLine(payload.drama || {}) ? h('p', { class: 'player-stats' }, socialStatLine(payload.drama || {})) : null]),
            h('button', { onClick: () => { downloadOpen.value = true } }, 'Watch on the app'),
          ]),
        ]),
        h('aside', { class: 'player-panel' }, [
          h('div', { class: 'player-panel-head' }, [h('div', [h('span', 'SERIES'), h('h2', 'Episodes')]), h('strong', String(episodes.length).padStart(2, '0'))]),
          h('div', { class: 'episode-list compact' }, episodes.map((episode: AnyRecord) => h('button', { class: { active: episode.id === selected?.id, 'episode-locked': !canWatchEpisode(episode) }, onClick: () => go(`/watch/${payload.drama.id}/${episode.episode_num}`) }, [h('span', [h('strong', String(episode.episode_num).padStart(2, '0')), h('small', `Episode ${episode.episode_num}`)]), h('small', episodeAccessLabel(episode, true))]))),
          appDownloadCard(true),
        ]),
      ])
    }
    function continueView() { const list = data.history?.list || []; const user = data.profile?.userInfo || data.profile || {}; const active = Boolean(user.vip || user.is_vip || user.vip_expire_time); return h('main', { class: 'page content-page' }, [h('div', { class: 'library-heading' }, [h('div', [h('span', 'YOUR ACCOUNT'), h('h1', 'Continue & My List')]), h('button', { class: 'manage-vip', onClick: () => go('/membership') }, 'Manage VIP')]), h('div', { class: 'account-layout' }, [h('section', [h('div', { class: 'section-heading' }, [h('div', [h('h2', 'Continue Watching'), h('p', 'Synced across devices')])]), h('div', { class: 'poster-grid account-poster-grid' }, list.map((item: AnyRecord) => dramaCard({ ...item.drama, __episode: item.watch_progress?.episode_num || 1 }, `Continue EP ${item.watch_progress?.episode_num || 1}`)))]), h('aside', { class: 'account-panel' }, [h('section', { class: 'account-vip' }, [h('div', [h('strong', active ? 'Reelora VIP active' : 'Reelora VIP'), h('small', active ? 'Current plan and renewal are shown by your account service' : 'See the plan currently available to this account')]), h('button', { onClick: () => go('/membership') }, 'Details')]), h('h2', 'Account states'), h('div', { class: 'account-state' }, [h('div', [h('strong', active ? 'Active member' : user.nickname ? 'Signed in' : 'Guest'), h('small', active ? 'Current plan and renewal' : user.nickname ? 'Viewing history sync is active' : 'Sign in to sync')]), h('span', 'CURRENT')])])])]) }
    function rewardsView() { const tasks = [...(data.rewards?.[1]?.once_tasks || []), ...(data.rewards?.[1]?.daily_tasks || [])]; return h('main', { class: 'page content-page' }, [h('div', { class: 'page-title' }, [h('span', 'REWARDS'), h('h1', 'Watch Together, Get More'), h('p', 'Check in daily, finish tasks and share a Friend Pass with friends in the Reelora app.')]), h('div', { class: 'task-grid' }, tasks.map((task: AnyRecord) => h('article', [h('span', task.task_type || 'task'), h('h3', task.name), h('p', task.description), h('strong', `${task.current_value || 0} / ${task.target_value || 0}`)]))), appDownloadCard()]) }
    function profileView() { const user = data.profile?.userInfo || data.profile || {}; return h('main', { class: 'page content-page profile-view' }, [h('div', { class: 'profile-avatar' }, (user.nickname || 'G').slice(0, 1)), h('span', 'PROFILE'), h('h1', user.nickname || 'Guest'), h('p', user.nickname ? 'Your Reelora account and library.' : 'Sign in to sync viewing history, favorites and membership across every screen.'), h('div', { class: 'profile-actions' }, [user.nickname ? null : h('button', { class: 'primary', onClick: () => go('/signin') }, 'Sign in to Reelora'), h('button', { class: 'secondary', onClick: () => go('/continue') }, 'Open Continue Watching')])]) }
    function signinView() { return h('main', { class: 'auth-layout' }, [
      h('section', { class: 'auth-art' }, [h('img', { src: '/subscription/paywall-hero-bound-by-fate.jpg', alt: '' }), h('div', [h('span', 'YOUR STORIES. YOUR SCREEN.'), h('h1', 'Come back to the moment you left.'), h('p', 'One Reelora account keeps your episodes, favorites and VIP access together.')])]),
      h('section', { class: 'auth-panel' }, [
        h('div', { class: 'auth-brand' }, [h('img', { src: '/brand/reelora-titanium-mark.png', alt: 'Reelora' }), h('strong', 'Reelora')]),
        h('span', 'WELCOME BACK'), h('h2', 'Sign in to continue'), h('p', 'Use the secure Reelora sign-in flow. You will return to your library after authentication.'),
        h('button', { class: 'auth-provider', onClick: () => { location.href = h5UrlWithAttribution('https://reelora.vip/pagesUser/login/login') } }, [h('img', { src: '/icons/google.png', alt: '' }), h('strong', 'Continue securely')]),
        h('button', { class: 'auth-email', onClick: () => { location.href = h5UrlWithAttribution('https://reelora.vip/pagesUser/email-login/email-login') } }, 'Continue with email'),
        h('small', ['Need help? ', h('a', { href: `mailto:${CONTACT_EMAIL}` }, CONTACT_EMAIL)]),
      ]),
    ]) }
    function membershipView() { const hero = modules.value.trending[0] || modules.value.heroes[0] || {}; return h('main', { class: 'page content-page membership-layout' }, [h('section', { class: 'membership-art' }, [h('img', { src: hero.cover_horizontal_image || hero.cover_vertical_image || hero.image || '', alt: '' }), h('div', [h('span', 'REELORA VIP'), h('h1', 'Keep every story going'), h('p', 'Unlimited dramas. No ads. No interruptions.')])]), h('section', { class: 'membership-checkout' }, [h('div', { class: 'membership-brand' }, [h('img', { src: '/brand/reelora-titanium-mark.png', alt: 'Reelora' }), h('strong', 'Reelora')]), h('h2', 'Start VIP'), h('p', vipPlan.value.available ? 'Amount and billing period below match web checkout.' : 'Choose a plan on the next step before checkout.'), h('div', { class: 'membership-plan' }, [h('span', 'CURRENT WEB PLAN'), h('strong', vipPlan.value.primary), h('small', vipPlan.value.renewal)]), h('div', { class: 'membership-benefits' }, [['Unlimited', 'Every drama'], ['Ad-Free', 'No interruptions'], ['Continue', 'Across devices']].map(([title, detail]) => h('div', [h('strong', title), h('small', detail)]))), h('div', { class: 'payment-methods' }, [h('span', 'Pay securely with Stripe'), h('span', 'HKPay')]), h('button', { class: 'membership-cta', onClick: () => { const planId = Number(vipPlan.value.planId || 0); const base = 'https://reelora.vip/pagesUser/subscribe/subscribe'; location.href = h5UrlWithAttribution(planId > 0 ? `${base}?plan_id=${planId}` : base) } }, vipPlan.value.cta || 'Continue to secure checkout'), h('small', { class: 'membership-terms' }, vipPlan.value.renewal)])]) }

    function currentView() {
      return { home: homeView, discover: discoverView, latest: latestView, categories: categoriesView, search: searchView, category: categoryView, ranking: rankingView, detail: detailView, player: playerView, membership: membershipView, continue: continueView, rewards: rewardsView, profile: profileView, signin: signinView }[route.value.view]()
    }

    onMounted(() => {
      persistPcAttributionFromUrl()
      window.addEventListener('popstate', onPop)
      window.addEventListener('scroll', onWindowScroll, { passive: true })
      heroTimer = setInterval(() => { if (!heroPaused.value && heroMediaCoordinator.canAutoAdvance() && modules.value.heroes.length > 1) void selectHero((heroIndex.value + 1) % modules.value.heroes.length) }, 5000)
      void load()
    })
    onBeforeUnmount(() => {
      window.removeEventListener('popstate', onPop)
      window.removeEventListener('scroll', onWindowScroll)
      if (heroTimer) clearInterval(heroTimer)
      heroMediaCoordinator.invalidate()
    })

    return () => h('div', { class: 'site-shell' }, [
      h('header', { class: 'site-header' }, [h('button', { class: 'brand', onClick: () => go('/') }, [h('img', { src: '/brand/reelora-titanium-mark.png', alt: 'Reelora' }), h('span', [h('strong', 'Reelora'), h('small', 'STORIES THAT MOVE YOU')])]), h('nav', ['Home', 'Discover', 'Latest', 'Ranking'].map(label => h('button', { class: { 'nav-button--active': navActive(label) }, onClick: () => go(label === 'Home' ? '/' : `/${label.toLowerCase()}`) }, label))), h('form', { class: 'header-search', onSubmit: submitSearch }, [h('img', { src: '/icons/search-w.png', alt: '' }), h('input', { value: searchDraft.value, placeholder: 'Search dramas', 'aria-label': 'Search dramas', onInput: (event: Event) => { searchDraft.value = (event.target as HTMLInputElement).value } }), h('button', { 'aria-label': 'Submit search' }, 'Search')]), h('button', { class: 'download-button', onClick: () => { downloadOpen.value = true } }, 'Get the app'), isVipAccount() ? null : h('button', { class: 'vip-button', onClick: () => go('/membership') }, 'VIP'), h('button', { class: 'profile-button profile-avatar-button', 'aria-label': 'Profile', onClick: () => go('/profile') }, [h('img', { src: '/brand/reelora-titanium-mark.png', alt: '' })])]),
      loading.value && route.value.view === 'home' && !data.home && !error.value ? homeSkeleton() : (error.value && !hasViewData() ? statePanel() : currentView()),
      h('footer', { class: 'site-footer' }, [
        h('div', { class: 'footer-brand' }, [h('img', { src: '/brand/reelora-titanium-mark.png', alt: '' }), h('span', [h('strong', 'Reelora'), h('small', 'Stories that move you')])]),
        h('div', { class: 'footer-links' }, [h('a', { href: '/privacy-policy.html' }, 'Privacy'), h('a', { href: '/account-deletion.html' }, 'Account deletion'), h('a', { href: `mailto:${CONTACT_EMAIL}` }, CONTACT_EMAIL)]),
        h('button', { class: 'footer-download', onClick: () => { downloadOpen.value = true } }, 'Scan to download the app'),
        h('small', '© 2026 Reelora · CREST TECH MEDIA LIMITED'),
      ]),
      downloadDialog(),
      qrDock(),
    ])
  },
})

createApp(App).mount('#app')
